import type { DateTimePicker } from '../datetimepicker';

export interface IInstanceManager {
  isInitialized(element: HTMLElement | string): boolean;
  getInstance(element: HTMLElement | string): DateTimePicker | undefined;
  destroy(element: HTMLElement | string): boolean;
  getAllInstances(): Map<string, DateTimePicker>;
}

class InstanceManagerImpl implements IInstanceManager {
  private instancesById = new Map<string, DateTimePicker>();
  private instancesByElement = new WeakMap<HTMLElement, DateTimePicker>();

  /**
   * Регистрирует экземпляр пикера
   */
  register(id: string | null, instance: DateTimePicker, elements: HTMLElement[] = []): void {
    if (id) {
      this.instancesById.set(id, instance);
    }

    elements.forEach(element => {
      this.instancesByElement.set(element, instance);
    });
  }

  /**
   * Удаляет экземпляр из хранилища
   */
  unregister(id: string | null, elements: HTMLElement[] = []): void {
    if (id) {
      this.instancesById.delete(id);
    }

    elements.forEach(element => {
      this.instancesByElement.delete(element);
    });
  }

  /**
   * Находит DOM-элемент по CSS selector, raw id или возвращает HTMLElement как есть.
   */
  resolveElement(element: HTMLElement | string): HTMLElement | null {
    if (element instanceof HTMLElement) {
      return element;
    }

    const selector = element.trim();
    if (selector === '') {
      return null;
    }

    const selected = this.querySelector(selector);
    if (selected instanceof HTMLElement) {
      return selected;
    }

    const id = selector.startsWith('#') ? selector.slice(1) : selector;
    return document.getElementById(id);
  }

  /**
   * Проверяет, инициализирован ли пикер на элементе
   */
  isInitialized(element: HTMLElement | string): boolean {
    return !!this.getInstance(element);
  }

  /**
   * Возвращает экземпляр пикера для элемента
   */
  getInstance(element: HTMLElement | string): DateTimePicker | undefined {
    const resolvedElement = this.resolveElement(element);
    if (!resolvedElement) {
      return typeof element === 'string' ? this.getInstanceById(element) : undefined;
    }

    return (
      this.instancesByElement.get(resolvedElement) ??
      this.getInstanceById(resolvedElement.dataset.dtpOriginalId) ??
      this.getInstanceById(resolvedElement.id) ??
      this.getInstanceFromHost(resolvedElement)
    );
  }

  /**
   * Уничтожает пикер на элементе
   */
  destroy(element: HTMLElement | string): boolean {
    const instance = this.getInstance(element);
    if (instance) {
      instance.destroy();
      return true;
    }
    return false;
  }

  /**
   * Возвращает все активные экземпляры
   */
  getAllInstances(): Map<string, DateTimePicker> {
    return new Map(this.instancesById);
  }

  private querySelector(selector: string): Element | null {
    try {
      return document.querySelector(selector);
    } catch {
      return null;
    }
  }

  private getInstanceById(id: string | undefined): DateTimePicker | undefined {
    if (!id) {
      return undefined;
    }

    const normalizedId = id.startsWith('#') ? id.slice(1) : id;
    return this.instancesById.get(normalizedId);
  }

  private getInstanceFromHost(element: HTMLElement): DateTimePicker | undefined {
    const host = element.closest('.dtp-host');
    if (!(host instanceof HTMLElement)) {
      return undefined;
    }

    return (
      this.instancesByElement.get(host) ??
      this.findInstanceByInputSelector(host, `.${DATE_TIME_PICKER_VALUE_INPUT_CLASS}`) ??
      this.findInstanceByInputSelector(host, `.${DATE_TIME_PICKER_DISPLAY_INPUT_CLASS}`)
    );
  }

  private findInstanceByInputSelector(
    host: HTMLElement,
    selector: string,
  ): DateTimePicker | undefined {
    const input = host.querySelector(selector);
    if (!(input instanceof HTMLElement)) {
      return undefined;
    }

    return (
      this.instancesByElement.get(input) ??
      this.getInstanceById(input.dataset.dtpOriginalId) ??
      this.getInstanceById(input.id)
    );
  }
}

const DATE_TIME_PICKER_DISPLAY_INPUT_CLASS = 'dtp-display-input';
const DATE_TIME_PICKER_VALUE_INPUT_CLASS = 'dtp-value-input';

// Экспортируем единственный экземпляр (синглтон)
export const instanceManager = new InstanceManagerImpl();
